import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Minus, X, Lock, Truck, ShieldCheck, Loader2, Clock, RotateCcw, Check, Sparkles, Zap, PackageCheck, ArrowRight, Star } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { fmt, applyPromo } from "@/data/catalog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Stars } from "@/components/common/ui";
import { PaymentIcons, AfterpayBadge } from "@/components/common/PaymentIcons";
import { CandleUpsell } from "@/components/product/CandleUpsell";
import { track } from "@/components/common/Analytics";
import { SocialProof } from "@/components/common/SocialProof";
import { getReservationSeconds, fmtClock } from "@/lib/timer";

const PROTECTION_PRICE = 19;

const STEPS = ["Bag", "Checkout"];

function Steps() {
  return (
    <div data-testid="cart-steps" className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <span className="inline-flex items-center gap-1.5 text-[0.78rem] font-medium text-[#FBC4A8]">
        <Sparkles className="h-3.5 w-3.5" /> You're almost there
      </span>
      <span className="hidden h-3.5 w-px bg-[#F2E8E6] sm:block" />
      <div className="flex items-center gap-2.5">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-2.5">
            <div className="flex items-center gap-2">
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[0.7rem] font-semibold ${i === 0 ? "bg-[#FBC4A8] text-white" : "border border-[#F2E8E6] bg-white text-[#9A8E88]"}`}>{i + 1}</span>
              <span className={`text-[0.78rem] ${i === 0 ? "font-medium text-[#2C2B2C]" : "text-[#9A8E88]"}`}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <span className="h-px w-5 bg-[#F2E8E6] sm:w-7" />}
          </div>
        ))}
      </div>
    </div>
  );
}

const REVIEWS = [
  { name: "Audrey S.", loc: "Gold Coast, QLD", text: "A glow I didn't have before. After 10 mins a day I can see a noticeable difference.", days: "3 weeks ago" },
  { name: "Sophie L.", loc: "Gold Coast, QLD", text: "Clearer skin in weeks. My texture and pigment improved. Wasn't expecting it so soon!", days: "1 month ago" },
  { name: "Denise H.", loc: "Brisbane, QLD", text: "Shocked at how much smoother my skin looks. The lines on my neck have softened.", days: "1 month ago" },
];

const REASSURANCE = [
  { icon: Truck, title: "Free express shipping", sub: "Tracked, dispatched in 1 day" },
  { icon: ShieldCheck, title: "30-day money back", sub: "Love it or your money back" },
  { icon: RotateCcw, title: "12-month warranty", sub: "Every device fully covered" },
];

function Countdown() {
  const [secs, setSecs] = useState(getReservationSeconds);
  useEffect(() => {
    const t = setInterval(() => setSecs(getReservationSeconds()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span data-testid="cart-countdown" className="inline-flex items-center gap-2 text-sm text-[#9A8E88]">
      <Clock className="h-4 w-4 text-[#FBC4A8]" />
      Bag reserved · <span className="font-semibold tabular-nums text-[#2C2B2C]">{fmtClock(secs)}</span>
    </span>
  );
}

function RatingPill() {
  return (
    <span className="inline-flex items-center gap-2" data-testid="cart-rating">
      <Stars n={5} className="h-3.5 w-3.5" />
      <span className="text-[0.78rem] text-[#2C2B2C]"><span className="font-semibold">4.9</span> · 1,000+ reviews</span>
    </span>
  );
}

export default function Cart() {
  const { detailed, subtotal, updateQty, removeItem, addItem, items } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savedEmail, setSavedEmail] = useState("");
  const [bagSaved, setBagSaved] = useState(false);

  const displayItems = detailed.filter((i) => i.sku !== "protection");
  const displayCount = displayItems.reduce((s, i) => s + i.quantity, 0);
  const hasProtection = items.some((i) => i.sku === "protection");

  const compareTotal = detailed.reduce((s, i) => s + (i.compareAt || i.price) * i.quantity, 0);
  const savings = Math.max(0, compareTotal - subtotal);
  const { total: grandTotal } = applyPromo(subtotal);

  const toggleProtection = () => {
    if (hasProtection) removeItem("protection");
    else addItem("protection", 1, false);
  };

  const handleRemove = (sku) => {
    const it = detailed.find((i) => i.sku === sku);
    if (!it) return;
    removeItem(sku);
    toast("Removed from your bag", {
      description: it.name,
      action: { label: "Undo", onClick: () => addItem(sku, it.quantity, false) },
    });
  };

  const saveBag = async (e) => {
    e.preventDefault();
    if (!savedEmail) return;
    try {
      await api.post("/newsletter", { email: savedEmail });
      setBagSaved(true);
      track("Lead", { content_name: "save_bag" });
      toast.success("Bag saved", { description: "We'll keep your glow ready and email you a link." });
    } catch {
      toast.error("Please enter a valid email.");
    }
  };

  const checkout = () => {
    if (items.length === 0) return;
    navigate("/checkout");
  };

  if (displayItems.length === 0) {
    return (
      <div className="bg-white">
        <div data-testid="cart-page" className="mx-auto flex min-h-[70vh] max-w-[1400px] flex-col items-center justify-center px-6 text-center">
          <h1 className="font-display text-5xl font-light text-[#2C2B2C]">Your bag is empty</h1>
          <p className="mt-4 text-[#9A8E88]">Discover the glow ritual everyone's talking about.</p>
          <Link to="/shop" data-testid="cart-empty-cta" className="mt-8 rounded-full bg-[#232222] px-10 py-4 text-xs uppercase tracking-[0.18em] text-[#FFFCFA]">Shop the Collection</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <SocialProof />
      <div data-testid="cart-page" className="mx-auto max-w-[1320px] px-5 py-5 pb-28 md:px-10 md:py-10 lg:pb-16">
        <div className="border-b border-[#FFFCFA] pb-4 md:pb-6">
          {/* Mobile: steps above heading */}
          <div className="mb-4 md:hidden">
            <Steps />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="font-display text-4xl font-light text-[#2C2B2C] sm:text-[2.75rem]">Your Bag <span className="align-middle text-base text-[#9A8E88]">({displayCount})</span></h1>
            <Countdown />
          </div>
          <div className="mt-5 hidden flex-wrap items-center justify-between gap-y-3 md:flex">
            <Steps />
            <span className="hidden sm:block"><RatingPill /></span>
          </div>
        </div>

        {/* Free-shipping unlock cue */}
        <div data-testid="cart-shipping-cue" className="mt-6 hidden flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#FFFCFA] px-5 py-4 md:flex">
          <p className="flex items-center gap-3 text-sm text-[#2C2B2C]">
            <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#FBC4A8]">
              <Check className="h-4 w-4 text-white" strokeWidth={3} />
            </span>
            <span><span className="font-semibold">Free express shipping unlocked</span>
            {savings > 0 && <> · you're saving <span className="font-semibold text-[#FBC4A8]" data-testid="cart-savings-amount">{fmt(savings)}</span> today</>}</span>
          </p>
          <span data-testid="cart-scarcity" className="flex items-center gap-1.5 text-xs font-medium text-[#FBC4A8]">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#FBC4A8]" /> Only 8 left in stock · 127 sold this week
          </span>
        </div>

        <div className="mt-6 grid items-start gap-x-16 gap-y-4 md:mt-8 md:gap-y-8 lg:grid-cols-[1.55fr_1fr] lg:gap-y-12">
          {/* LEFT */}
          <div className="space-y-8 md:space-y-12">
            <div>
              {displayItems.map((it, idx) => (
                <div key={it.sku} data-testid={`cart-row-${it.sku}`} className={`flex gap-4 py-5 md:gap-6 md:py-7 ${idx > 0 ? "border-t border-[#FFFCFA]" : ""}`}>
                  <div className="h-24 w-24 flex-none overflow-hidden rounded-2xl bg-[#FFFCFA] md:h-28 md:w-28">
                    <img src={it.image} alt={it.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-xl text-[#2C2B2C] md:text-2xl">{it.name}</p>
                        <p className="mt-1.5 hidden text-sm text-[#9A8E88] md:block">{it.subtitle}</p>
                        {idx === 0 && (
                          <p data-testid="cart-scarcity-mobile" className="mt-2 flex items-center gap-1.5 text-[0.74rem] font-medium text-[#E97C6A] md:hidden">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#E97C6A]" /> Only 8 left in stock
                          </p>
                        )}
                      </div>
                      <button onClick={() => handleRemove(it.sku)} data-testid={`cart-remove-${it.sku}`} className="rounded-full p-1.5 text-[#9A8E88] transition-colors hover:bg-[#FFFCFA] hover:text-[#2C2B2C]"><X className="h-4 w-4" /></button>
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-5">
                      <div className="flex items-center rounded-full border border-[#F2E8E6]">
                        <button onClick={() => updateQty(it.sku, it.quantity - 1)} data-testid={`cart-dec-${it.sku}`} className="px-3.5 py-2 text-[#9A8E88] transition-colors hover:text-[#2C2B2C]"><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-9 text-center text-sm font-medium">{it.quantity}</span>
                        <button onClick={() => updateQty(it.sku, it.quantity + 1)} data-testid={`cart-inc-${it.sku}`} className="px-3.5 py-2 text-[#9A8E88] transition-colors hover:text-[#2C2B2C]"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="text-right">
                        {it.compareAt > it.price && (
                          <span className="mr-2 text-sm text-[#9A8E88] line-through">{fmt(it.compareAt * it.quantity)}</span>
                        )}
                        <span className="font-display text-xl text-[#2C2B2C] md:text-2xl">{fmt(it.price * it.quantity)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <CandleUpsell soft />

            <div className="hidden md:block">
              <div data-testid="cart-reassurance" className="mb-6 hidden grid-cols-3 gap-3 border-b border-[#FFFCFA] pb-6 md:mb-8 md:grid md:gap-x-8 md:gap-y-4 md:pb-8">
                {REASSURANCE.map((r) => (
                  <div key={r.title} className="flex flex-col items-center gap-1.5 text-center md:flex-row md:gap-3 md:text-left">
                    <r.icon className="h-5 w-5 flex-none text-[#FBC4A8]" strokeWidth={1.75} />
                    <div>
                      <p className="text-[0.7rem] font-semibold leading-tight text-[#2C2B2C] md:text-[0.82rem]">{r.title}</p>
                      <p className="hidden text-xs text-[#9A8E88] md:block">{r.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Review cards — hidden on mobile (SocialProof + rating pill already shown up top) */}
              <div className="hidden sm:block">
                <div className="flex items-center gap-2">
                  <Stars n={5} className="h-4 w-4" />
                  <p className="text-sm text-[#2C2B2C]"><span className="font-semibold">4.9</span> · loved by 1,000+ women</p>
                </div>
                <div className="mt-6 grid gap-5 sm:grid-cols-3">
                  {REVIEWS.map((r) => (
                    <div key={r.name} data-testid={`cart-review-${r.name.split(" ")[0].toLowerCase()}`} className="rounded-2xl bg-[#FFFCFA] p-5">
                      <Stars n={5} className="h-3.5 w-3.5" />
                      <p className="mt-3 text-sm leading-relaxed text-[#2C2B2C]">"{r.text}"</p>
                      <p className="mt-3 text-xs text-[#9A8E88]">{r.name} · {r.days}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-3xl border border-[#FFFCFA] p-8 shadow-[0_28px_70px_-46px_rgba(44,43,44,0.4)]">
              <h2 className="font-display text-2xl text-[#2C2B2C]">Order Summary</h2>

              {/* Order bump */}
              <button onClick={toggleProtection} data-testid="order-bump-toggle"
                className={`mt-5 flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition-all ${hasProtection ? "border-[#FBC4A8] bg-[#FFFCFA]" : "border-[#F2E8E6] bg-white hover:border-[#F0C9B4]"}`}>
                <span className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-md border-2 transition-colors ${hasProtection ? "border-[#FBC4A8] bg-[#FBC4A8]" : "border-[#D8CCC2]"}`}>
                  {hasProtection && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
                </span>
                <span className="flex-1">
                  <span className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#2C2B2C]">Add VIP Order Protection</span>
                    <span className="text-sm font-semibold text-[#FBC4A8]">+{fmt(PROTECTION_PRICE)}</span>
                  </span>
                  <span className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#8A7F86]">
                    <span className="inline-flex items-center gap-1"><PackageCheck className="h-3.5 w-3.5 text-[#FBC4A8]" /> Shipping protection</span>
                    <span className="inline-flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-[#FBC4A8]" /> Priority processing</span>
                    <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-[#FBC4A8]" /> 2-year warranty</span>
                  </span>
                </span>
              </button>

              <div className="mt-5 space-y-3.5 text-sm">
                {savings > 0 && (
                  <div className="flex justify-between text-[#9A8E88]"><span>Original price</span><span className="line-through" data-testid="summary-compare">{fmt(compareTotal)}</span></div>
                )}
                {savings > 0 && (
                  <div className="flex items-center justify-between" data-testid="summary-promo">
                    <span className="flex items-center gap-1.5 text-[#3B8C6E]">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} /> Bundle savings
                    </span>
                    <span className="font-medium text-[#3B8C6E]" data-testid="summary-savings">- {fmt(savings)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#9A8E88]"><span>Express shipping</span><span className="font-medium text-[#4A5D4E]">FREE</span></div>
              </div>
              <div className="mt-6 flex items-baseline justify-between border-t border-[#FFFCFA] pt-6">
                <span className="text-sm uppercase tracking-[0.15em] text-[#2C2B2C]">Total</span>
                <span className="font-display text-3xl text-[#2C2B2C]" data-testid="summary-total">{fmt(grandTotal)}</span>
              </div>

              <button onClick={checkout} disabled={loading} data-testid="checkout-button"
                className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#232222] py-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#FFFCFA] transition-all hover:bg-[#3a3636] hover:tracking-[0.2em] disabled:opacity-60">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : <><Lock className="h-3.5 w-3.5" /> Secure Checkout</>}
              </button>

              <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-[#2C2B2C]" data-testid="cart-afterpay">
                or 4 payments of <span className="font-semibold">{fmt(grandTotal / 4)}</span> with <AfterpayBadge />
              </div>

              <div className="mt-5 hidden items-center gap-3 rounded-2xl bg-[#FFFCFA] px-4 py-3.5 md:flex" data-testid="cart-guarantee">
                <ShieldCheck className="h-6 w-6 flex-none text-[#FBC4A8]" strokeWidth={1.75} />
                <div>
                  <p className="text-[0.82rem] font-semibold leading-tight text-[#2C2B2C]">Try it risk-free for 30 nights</p>
                  <p className="text-xs text-[#9A8E88]">Love your glow or get a full refund. No questions asked.</p>
                </div>
              </div>

              <div className="mt-6 hidden border-t border-[#FFFCFA] pt-6 md:block">
                <p className="mb-3 text-center text-[0.6rem] uppercase tracking-[0.18em] text-[#9A8E88]">Guaranteed safe checkout</p>
                <PaymentIcons className="flex-nowrap justify-center" />
              </div>
            </div>
            <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-[#9A8E88]"><Lock className="h-3 w-3" /> Encrypted &amp; securely processed by Stripe</p>
          </div>
        </div>

        {/* Trust stack + safe checkout — mobile only, bottom of page (matches Checkout) */}
        <div data-testid="cart-reassurance-mobile" className="mt-6 divide-y divide-[#F2E8E6] overflow-hidden rounded-2xl border border-[#F2E8E6] bg-gradient-to-b from-white to-[#FFFAF6] md:hidden">
          {[
            { icon: <Truck className="h-[18px] w-[18px]" strokeWidth={1.9} />, title: "Free express shipping", sub: "Tracked & dispatched within 1 business day" },
            { icon: <Star className="h-[18px] w-[18px] fill-current" strokeWidth={1.9} />, title: "4.9★ · loved by 1,000+ women", sub: "Rated 5 stars by verified buyers" },
            { icon: <ShieldCheck className="h-[18px] w-[18px]" strokeWidth={1.9} />, title: "30-night risk-free trial", sub: "Love your glow or get a full refund" },
          ].map((b) => (
            <div key={b.title} className="flex items-center gap-3.5 px-4 py-3.5">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#FDEBE0] text-[#E6976F] ring-1 ring-inset ring-[#FBD9C6]">
                {b.icon}
              </span>
              <div className="min-w-0">
                <p className="text-[0.84rem] font-semibold leading-snug text-[#2C2B2C]">{b.title}</p>
                <p className="text-xs leading-snug text-[#9A8E88]">{b.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Guaranteed safe checkout — mobile only */}
        <div className="mt-6 border-t border-[#F2E8E6] pt-5 md:hidden">
          <p className="mb-3 text-center text-[0.6rem] uppercase tracking-[0.18em] text-[#9A8E88]">Guaranteed safe checkout</p>
          <PaymentIcons className="flex-nowrap justify-center" />
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-[60] border-t border-[#FFFCFA] bg-white/95 px-4 py-3 backdrop-blur lg:hidden" data-testid="cart-sticky-bar">
        <button onClick={checkout} disabled={loading} data-testid="checkout-button-mobile"
          className="flex w-full items-center justify-between rounded-full bg-[#232222] px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#FFFCFA] disabled:opacity-60">
          <span className="flex items-center gap-2">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-3.5 w-3.5" />} Secure Checkout</span>
          <span className="font-display text-base tracking-normal">{fmt(grandTotal)}</span>
        </button>
      </div>
    </div>
  );
}

